<template>
  <Modal
    ref="modal"
    title="New AIM Version"
  >
    <div v-if="!canCreate">
      <p>Latest version must be saved before creating a new version!</p>
      <button
        class="btn btn-default"
        @click="cancel"
      >
        Cancel
      </button>
    </div>
    <BaseForm
      v-else
      submit-title="Submit"
      :loading="loading"
      @submit="submit"
      @cancel="cancel"
    >
      <label>Create new version from:</label>
      <div class="form-group">
        <div class="custom-control custom-radio">
          <input
            id="customRadio2"
            v-model="fromAttachment"
            :value="true"
            type="radio"
            name="customRadio2"
            class="custom-control-input"
          >
          <label
            class="custom-control-label"
            for="customRadio2"
          >
            Project Attachment
          </label>
        </div>
      </div>
      <template v-if="fromAttachment">
        <BaseSelect
          v-model="filename"
          :options="filenames"
          title="Filename"
          name="filename"
          required
        />
        <BaseSelect
          v-model="attachment"
          :options="filteredAttachments"
          :custom-label="attachmentLabel"
          title="Version (upload date)"
          name="attachment"
          label="created_on"
          track-by="id"
          open-direction="bottom"
          required
        />
      </template>
      <template v-if="latestVersion">
        <div class="form-group">
          <div class="custom-control custom-radio">
            <input
              id="customRadio1"
              v-model="fromAttachment"
              :value="false"
              type="radio"
              name="customRadio1"
              class="custom-control-input"
            >
            <label
              class="custom-control-label"
              for="customRadio1"
            >
              Current version
            </label>
          </div>
        </div>
      </template>
      <BaseInputCheckbox
        v-if="errors && errors.length > 0"
        v-model="ignoreErrors"
        title="Ignore Errors"
        name="ignore_errors"
      />
      <FieldErrorList v-model="errors" />
    </BaseForm>
  </Modal>
</template>

<script>
/**
 * a Modal component that creates a new AIM.
 * 'create-cm' event with aim is emitted when created.
 */
import Modal from '@/components/Modal'
import FieldErrorList from '@/components/FieldErrorList'
import aimAPI from '@/api/aim'
import projectAPI from '@/api/project'
import formatDateTime from '@/filters/formatDateTime'

export default {
  components: {
    FieldErrorList,
    Modal,
  },
  props: {
    aimId: {
      type: [String, Number],
      required: true,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
    latestVersion: {
      type: [String, Number],
      required: true,
    },
    canCreate: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      filename: '',
      fromAttachment: true,
      attachment: null,
      attachments: [],
      errors: [],
      ignoreErrors: false,
      loading: false,
    }
  },
  computed: {
    // get valid unique sorted filenames
    filenames () {
      return this.attachments
        .map(a => a.filename)
        .filter(fn => fn.endsWith('.xlsx') || fn.endsWith('.xlsm'))
        .sort((a, b) => a.localeCompare(b))
        .filter((fn, i, fnlist) => i === fnlist.indexOf(fn))
    },
    // get attachments matching filename, sorted by newest first
    filteredAttachments () {
      return this.attachments
        .filter(a => a.filename === this.filename)
        .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
    },
  },
  beforeMount () {
    this.getData()
  },
  methods: {
    getData () {
      projectAPI.getAttachments(this.projectId).then(resp => {
        this.attachments = resp.data
      }).catch(error => {
        this.errors.push(error.message)
      })
    },
    attachmentLabel (a) {
      if (!a.id) { return '' }
      return formatDateTime(a.created_on)
    },
    reset () {
      this.fromAttachment = true
      this.filename = ''
      this.attachment = null
      this.errors = []
      this.ignoreErrors = false
    },
    cancel () {
      this.$refs.modal.hide()
      this.reset()
    },
    /**
     * @description open modal
     */
    show () {
      this.reset()
      this.$refs.modal.show()
    },
    submit () {
      if (this.fromAttachment) {
        this.createVersion()
      } else {
        this.copyVersion()
      }
    },
    createVersion () {
      const version = {
        attachment_id: this.attachment.id,
        descr: 'Attachment: ' + this.attachment.filename,
        ignore_errors: this.ignoreErrors,
      }
      this.loading = true
      aimAPI.createVersion(this.aimId, version).then(resp => {
        this.cancel()
        const params = { number: resp.number, sheetNumber: 0 }
        this.$router.push({ name: 'list_aim_rules', params: params })
        this.$emit('create-aim-version', resp)
        this.loading = false
      }).catch(error => {
        if (error && error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors
        } else {
          this.errors = [error.message]
        }
        this.loading = false
      })
    },
    copyVersion () {
      this.loading = true
      aimAPI.copyVersion(this.aimId).then(resp => {
        this.cancel()
        const params = { number: resp.number, sheetNumber: 0 }
        this.$router.push({ name: 'list_aim_rules', params: params })
        this.$emit('create-aim-version', resp)
        this.loading = false
      }).catch(error => {
        this.errors = [error.message]
        this.loading = false
      })
    },
  },
}
</script>

<style scoped>
.custom-control-label {
  font-weight: normal;
}
</style>
